/**
 * Type → shape / colour / status mappings, lifted verbatim from the
 * design handoff (`environment/tokens.jsx`):
 *   - NODE_SHAPE     : node type → SVG primitive
 *   - NODE_COLOUR    : node type → CSS variable name
 *   - STATUS         : node status → dot colour + label
 *
 * Cycle-102 sprint-2 ships the 6 in-scope types only; the remaining 8
 * (knowledge, proposal, miroshark, paradox, scenario, calibrate,
 * dossier, question, claim) are deferred to cycle-102.5 sprint-3 per
 * AC#7 + R-SDD-9.
 */

import type { NodeShape } from '../areaScale';

export type NodeType =
  | 'investigation'
  | 'forge'
  | 'theatre'
  | 'construct'
  | 'run'
  | 'attestation';

export type NodeStatus =
  | 'idle'
  | 'queued'
  | 'evaluating'
  | 'running'
  | 'completed'
  | 'failed'
  | 'certified'
  | 'provisional'
  | 'validated'
  | 'stale'
  | 'active';

/** From design handoff `environment/tokens.jsx:NODE_SHAPE`. */
export const NODE_TYPE_TO_SHAPE: Record<NodeType, NodeShape> = {
  investigation: 'pentagon',
  forge: 'circle',
  theatre: 'hex',
  construct: 'hex',
  run: 'diamond',
  attestation: 'star',
};

/** From design handoff `environment/tokens.jsx:NODE_COLOUR`. */
export const NODE_TYPE_TO_COLOUR_VAR: Record<NodeType, string> = {
  investigation: 'var(--sig-mint)',
  forge: 'var(--sig-mint)',
  theatre: 'var(--sig-iris)',
  construct: 'var(--sig-iris)',
  run: 'var(--sig-azure)',
  attestation: 'var(--sig-amber)',
};

/** From design handoff `environment/tokens.jsx:STATUS`. */
export const STATUS_DOT_VAR: Record<NodeStatus, string> = {
  idle: 'var(--fg-dim)',
  queued: 'var(--sig-amber)',
  evaluating: 'var(--sig-azure)',
  running: 'var(--sig-azure)',
  completed: 'var(--sig-mint)',
  failed: 'var(--sig-rose)',
  certified: 'var(--sig-amber)',
  provisional: 'var(--sig-amber)',
  validated: 'var(--sig-mint)',
  stale: 'var(--fg-dim)',
  active: 'var(--sig-iris)',
};

/**
 * Per-status fill opacity. Idle/stale read as ghosted; everything else
 * is solid. Mirrors handoff `detail.jsx:840`.
 */
export const STATUS_FILL_OPACITY: Record<NodeStatus, number> = {
  idle: 0.45,
  queued: 0.9,
  evaluating: 0.9,
  running: 0.9,
  completed: 0.9,
  failed: 0.9,
  certified: 0.9,
  provisional: 0.9,
  validated: 0.9,
  stale: 0.45,
  active: 0.9,
};
